import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { templateAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
    ArrowLeft, 
    Save, 
    Loader2,
    Building,
    FileText,
    Star
} from 'lucide-react';

export const AdminTemplate = () => {
    const navigate = useNavigate();
    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchTemplate();
    }, []);

    const fetchTemplate = async () => {
        try {
            const response = await templateAPI.get();
            setTemplate(response.data);
        } catch (error) {
            console.error('Failed to fetch template:', error);
            toast.error('Failed to load template');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setTemplate({ ...template, [field]: value });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await templateAPI.update(template);
            toast.success('Template saved!');
        } catch (error) {
            console.error('Failed to save template:', error);
            toast.error('Failed to save template');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="space-y-4">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-64 rounded-lg" />
                    <Skeleton className="h-64 rounded-lg" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-4 max-w-2xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-3">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate('/settings')}
                        className="h-10 w-10"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div className="flex-1">
                        <h1 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-900">
                            Quote Template
                        </h1>
                        <p className="text-slate-500 text-sm">Branding & Terms</p>
                    </div>
                </div>

                {/* Company Details */}
                <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                        <CardTitle className="font-heading text-lg uppercase tracking-tight flex items-center gap-2">
                            <Building className="h-5 w-5 text-red-600" />
                            Company Details
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                Company Name
                            </Label>
                            <Input
                                value={template?.company_name || ''}
                                onChange={(e) => handleChange('company_name', e.target.value)}
                                className="h-12 border-slate-300"
                                data-testid="company-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                Address
                            </Label>
                            <Textarea
                                value={template?.company_address || ''}
                                onChange={(e) => handleChange('company_address', e.target.value)}
                                rows={2}
                                className="border-slate-300"
                                data-testid="company-address-input"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                    Phone
                                </Label>
                                <Input
                                    value={template?.company_phone || ''}
                                    onChange={(e) => handleChange('company_phone', e.target.value)}
                                    className="h-12 border-slate-300"
                                    data-testid="company-phone-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                    Email
                                </Label>
                                <Input
                                    value={template?.company_email || ''}
                                    onChange={(e) => handleChange('company_email', e.target.value)}
                                    className="h-12 border-slate-300"
                                    data-testid="company-email-input"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                Website
                            </Label>
                            <Input
                                value={template?.company_website || ''}
                                onChange={(e) => handleChange('company_website', e.target.value)}
                                placeholder="www.yourcompany.co.uk"
                                className="h-12 border-slate-300"
                                data-testid="company-website-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                Company Registration Number
                            </Label>
                            <Input
                                value={template?.company_registration || ''}
                                onChange={(e) => handleChange('company_registration', e.target.value)}
                                placeholder="e.g. 12345678"
                                className="h-12 border-slate-300"
                                data-testid="company-registration-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                Quote Validity (Days)
                            </Label>
                            <Input
                                type="number"
                                value={template?.quote_validity_days || 30}
                                onChange={(e) => handleChange('quote_validity_days', parseInt(e.target.value) || 30)}
                                className="h-12 border-slate-300 w-32"
                                data-testid="validity-days-input"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Assumptions & Exclusions */}
                <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                        <CardTitle className="font-heading text-lg uppercase tracking-tight flex items-center gap-2">
                            <FileText className="h-5 w-5 text-red-600" />
                            Assumptions & Exclusions
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Textarea
                            value={template?.standard_assumptions_exclusions || ''}
                            onChange={(e) => handleChange('standard_assumptions_exclusions', e.target.value)}
                            rows={10}
                            className="border-slate-300 font-mono text-sm"
                            placeholder="ASSUMPTIONS:&#10;- All areas accessible...&#10;&#10;EXCLUSIONS:&#10;- Painting and decorating..."
                            data-testid="assumptions-textarea"
                        />
                    </CardContent>
                </Card>

                {/* Terms & Conditions */}
                <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="font-heading text-lg uppercase tracking-tight flex items-center gap-2">
                                <FileText className="h-5 w-5 text-red-600" />
                                Terms & Conditions
                            </CardTitle>
                            <div className="flex items-center gap-2">
                                <Label className="text-xs text-slate-500">Version:</Label>
                                <Input
                                    value={template?.terms_version || '1.0'}
                                    onChange={(e) => handleChange('terms_version', e.target.value)}
                                    className="h-8 w-20 text-sm border-slate-300"
                                    data-testid="terms-version-input"
                                />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <Textarea
                            value={template?.terms_and_conditions || ''}
                            onChange={(e) => handleChange('terms_and_conditions', e.target.value)}
                            rows={15}
                            className="border-slate-300 font-mono text-sm"
                            placeholder="TERMS AND CONDITIONS&#10;&#10;1. PAYMENT TERMS&#10;..."
                            data-testid="terms-textarea"
                        />
                    </CardContent>
                </Card>

                {/* Footer Text */}
                <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                        <CardTitle className="font-heading text-lg uppercase tracking-tight">
                            Footer Text
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Input
                            value={template?.footer_text || ''}
                            onChange={(e) => handleChange('footer_text', e.target.value)}
                            placeholder="Thank you for your business!"
                            className="h-12 border-slate-300"
                            data-testid="footer-text-input"
                        />
                    </CardContent>
                </Card>

                {/* Google Review Automation */}
                <Card className="bg-white border-slate-200 shadow-sm border-l-4 border-l-yellow-400">
                    <CardHeader className="pb-3">
                        <CardTitle className="font-heading text-lg uppercase tracking-tight flex items-center gap-2">
                            <Star className="h-5 w-5 text-yellow-500" />
                            Google Review Automation
                        </CardTitle>
                        <p className="text-slate-500 text-xs mt-1">
                            This message is shown when an invoice is marked as Paid, so you can quickly share with the customer.
                        </p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                Google Review Link
                            </Label>
                            <Input
                                value={template?.review_google_link || ''}
                                onChange={(e) => handleChange('review_google_link', e.target.value)}
                                placeholder="https://g.page/r/your-business/review"
                                className="h-12 border-slate-300"
                                data-testid="review-google-link-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                Review Request Message Template
                            </Label>
                            <Textarea
                                value={template?.review_template_message || ''}
                                onChange={(e) => handleChange('review_template_message', e.target.value)}
                                rows={5}
                                placeholder={`Hi [Customer Name],\n\nThank you for choosing us for your plastering work! We'd love to hear your feedback.\n\nLeave us a Google review here: [Google Review Link]\n\nThanks again!`}
                                className="border-slate-300 text-sm"
                                data-testid="review-message-input"
                            />
                            <p className="text-xs text-slate-400">
                                The review link will be automatically appended if not already included in the message.
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* Save Button */}
                <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-semibold uppercase tracking-wide"
                    data-testid="save-template-btn"
                >
                    {saving ? (
                        <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Saving...
                        </>
                    ) : (
                        <>
                            <Save className="mr-2 h-5 w-5" />
                            Save Template
                        </>
                    )}
                </Button>
            </div>
        </Layout>
    );
};
